import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { platformAdminsApi } from "@/lib/api/platformAdmins";
import { ApiRequestError } from "@/lib/api/client";
import { Button } from "@/components/ui/Button";
import { Input, PasswordInput } from "@/components/ui/Input";
import { ErrorMessage } from "@/components/ui/ErrorMessage";

/**
 * Public Super Admin activation - "Super Admin B activates account, B
 * creates their own password" (Section 3). Reached from the one-time
 * activation key an existing Super Admin generated and handed over out of
 * band; unauthenticated by design, see PlatformAdminService.Redeem on the
 * backend. After activation the new administrator signs in normally
 * (MFA setup, if required, happens on that first sign-in - see
 * MFASetupPanel), never here.
 */
export function PlatformAdminActivationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [activated, setActivated] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{ key: string; password: string; confirm: string }>({
    defaultValues: { key: params.get("key") ?? "", password: "", confirm: "" },
  });

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: (values: { key: string; password: string }) => platformAdminsApi.redeemActivation(values.key, values.password),
    onSuccess: () => setActivated(true),
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        for (const field of error.appError.fields ?? []) {
          if (field.field === "password") setError("password", { message: field.message });
        }
      }
    },
  });

  if (activated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-brand px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
          <h1 className="text-lg font-semibold text-slate-900">Account activated</h1>
          <p className="mt-2 text-sm text-slate-500">
            Your password has been set. Sign in to continue - if this account requires multi-factor authentication,
            you will be asked to set it up on your first sign-in.
          </p>
          <Button className="mt-4" fullWidth onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-brand px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-white">Activate your Super Admin account</h1>
          <p className="mt-1 text-sm text-primary-200">Enter your activation key and choose a password.</p>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-2xl">
          <form
            onSubmit={handleSubmit((values) => {
              if (values.password !== values.confirm) {
                setError("confirm", { message: "Passwords do not match" });
                return;
              }
              mutation.mutate(values);
            })}
            className="flex flex-col gap-4"
          >
            <Input label="Activation key" required error={errors.key?.message} {...register("key", { required: true })} />
            <PasswordInput
              label="New password"
              autoComplete="new-password"
              error={errors.password?.message}
              {...register("password", { required: true, minLength: 10 })}
            />
            <PasswordInput
              label="Confirm password"
              autoComplete="new-password"
              error={errors.confirm?.message}
              {...register("confirm", { required: true })}
            />
            {mutation.isError && <ErrorMessage error={mutation.error} />}
            <Button type="submit" loading={mutation.isPending} fullWidth className="mt-1">
              Activate account
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-primary-300">
          Already activated? <Link to="/login" className="underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
